import React from 'react';
import { FormSpy, AnyObject, FieldInputProps } from 'react-final-form';
import { FormApi } from 'final-form';
import clsx from 'clsx';
import { Grid, Typography, TextField, Checkbox, FormControlLabel, AccordionSummary, AccordionDetails } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { CurrencyInput } from '../../../components/CurrencyInput';
import { Rating } from '@material-ui/lab';
import { formatNumber } from '../../../helpers/ChartHelper';
import { TranslationTextList } from '../../../locales/common';
import StarRateIcon from '@material-ui/icons/StarRate';
import type { Enquiry, EnquiryFormClasses } from '../EnquiryFormDrawer';
import { arePropsEqualIgnoringForm } from './arePropsEqualIgnoringForm';
// DEBUG: Field / Accordion are the original components unless the render-count flag is on (see helpers/renderCountDebug)
import { Field, createRenderCountAccordion } from '../../../helpers/renderCountDebug';

const Accordion = createRenderCountAccordion('EnquiryValuePanel');

// Explicit request (EnquiryFormDrawer split): the "Enquiry Value" accordion (estimated fees / disbursements, quality, likelihood).
// Moved out of EnquiryFormDrawer.tsx with its JSX and every field's name / validate / subscription /
// handler logic unchanged. The outer component subscribes to the form (FormSpy) and passes the memoized
// body only the values it displays; event handlers read the form's values at the moment the event fires
// - exactly what the render-time `values` closure held before.
export interface EnquiryValuePanelProps {
    form: FormApi<Enquiry>;
    classes: EnquiryFormClasses;
    currency: string;
    translate: (key: keyof TranslationTextList) => string;
    isExpanded: boolean;
    onExpandChange: (event: React.ChangeEvent<{}>, expanded: boolean) => void;
    onRatingChange: (name: string, isDirectCallMutation: boolean, input: FieldInputProps<any, HTMLElement>, form: FormApi<Enquiry>, values: AnyObject) => (event: React.ChangeEvent<{}>, value: number | null) => void;
}

interface EnquiryValuePanelBodyProps extends EnquiryValuePanelProps {
    formValues: AnyObject;
}

const EnquiryValuePanelBody: React.FC<EnquiryValuePanelBodyProps> = React.memo((bodyProps) => {
    const {
        form, classes, currency, translate, isExpanded, onExpandChange, onRatingChange,
        formValues,
    } = bodyProps;
    const values = formValues;

    return (
        <Accordion
            className={classes.expansionPanel}
            expanded={isExpanded}
            onChange={onExpandChange}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="enquiry-value-content"
                id="enquiry-value-header"
                className={classes.expansionPanelSummary}
            >
                <div className={classes.sectionHeaderRow}>
                    <span className={clsx(classes.sectionIconChip, classes.sectionIconChipOrange)}>
                        <StarRateIcon fontSize="small" />
                    </span>
                    <Typography className={clsx(classes.heading, classes.sectionLabelOrange, classes.sectionLabelText)}>
                        {translate('Enquiry Value')}
                    </Typography>
                </div>
                <div className={classes.infoContainer}>
                    <div className="col">
                        <Field name="enquiryQuality">
                            {(
                                { input } /* FieldRenderProps */
                            ) => {
                                // tslint:disable-next-line: radix
                                return parseInt(input.value) > 0 && (
                                    <div className="value">
                                        <Rating
                                            // tslint:disable-next-line: radix
                                            value={parseInt(input.value)}
                                            readOnly={true}
                                        />
                                    </div>
                                );
                            }}
                        </Field>
                    </div>
                    <div className="col">
                        <Field name="estimatedFees">
                            {(
                                { input } /* FieldRenderProps */
                            ) => {
                                return parseFloat(input.value) > 0 && (
                                    <div className="value">
                                        {/* tslint:disable-next-line: radix */}
                                        {`${currency}${formatNumber(parseFloat(input.value), 2)}`}
                                    </div>
                                );
                            }}
                        </Field>
                    </div>
                </div>
            </AccordionSummary>
            <AccordionDetails
                className={classes.expandablePanelDetails}
            >
                <Grid container={true} spacing={2}>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <div className={classes.fieldAndCheckbox}>
                            <Field
                                name="estimatedFees"
                                label="Estimated Fees"
                                variant="outlined"
                                size="small"
                                // helperText={'Debtors amount to write off'}
                                subscription={{ touched: true, error: true, value: true }}
                                prefix={currency}
                                // className={classes.textField}
                                component={CurrencyInput}
                            // disabled={props.formAction === 'edit'}
                            />
                            <Field
                                name="isEstimatedFeesIncludesTax"
                                type="checkbox"
                                subscription={{ touched: true, error: true, value: true }}
                            >
                                {({ input, meta }) => (
                                    <FormControlLabel
                                        className={classes.formControlLabel}
                                        control={
                                            <Checkbox
                                                {...input}
                                                checked={values.isEstimatedFeesIncludesTax}
                                                onChange={input.onChange}
                                                checkedIcon={<CheckBoxIcon color="primary" />}
                                            />
                                        }
                                        label="Includes Tax"
                                    />
                                )}
                            </Field>
                        </div>
                    </Grid>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <div className={classes.fieldAndCheckbox}>
                            <Field
                                name="estimatedDisbursements"
                                label="Estimated Disbs"
                                variant="outlined"
                                size="small"
                                // helperText={'Debtors amount to write off'}
                                subscription={{ touched: true, error: true, value: true }}
                                prefix={currency}
                                // className={classes.textField}
                                component={CurrencyInput}
                            // disabled={props.formAction === 'edit'}
                            />
                            <Field
                                name="isEstimatedDisbursementsIncludesTax"
                                type="checkbox"
                                subscription={{ touched: true, error: true, value: true }}
                            >
                                {({ input, meta }) => (
                                    <FormControlLabel
                                        className={classes.formControlLabel}
                                        control={
                                            <Checkbox
                                                {...input}
                                                checked={values.isEstimatedDisbursementsIncludesTax}
                                                onChange={input.onChange}
                                                checkedIcon={<CheckBoxIcon color="primary" />}
                                            />
                                        }
                                        label="Includes Tax"
                                    />
                                )}
                            </Field>
                        </div>
                    </Grid>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="enquiryQuality"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <div className={classes.ratingWrapper}>
                                    <Typography component="legend">{translate('Enquiry Quality')}</Typography>
                                    <Rating
                                        {...input}
                                        value={input.value}
                                        onChange={(event: React.ChangeEvent<{}>, value: number | null) => onRatingChange('enquiryQuality', false, input, form, form.getState().values)(event, value)}
                                    />
                                </div>
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="likelihoodToProceed"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <div className={classes.ratingWrapper}>
                                    <Typography component="legend">Likelihood to Proceed</Typography>
                                    <Rating
                                        {...input}
                                        value={input.value}
                                        onChange={(event: React.ChangeEvent<{}>, value: number | null) => onRatingChange('likelihoodToProceed', false, input, form, form.getState().values)(event, value)}
                                    />
                                </div>
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="enquiryNotes"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label={translate('Sales Notes')}
                                    type="text"
                                    multiline={true}
                                    autoComplete="abcd"
                                    className={classes.textField}
                                />
                            )}
                        </Field>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}, arePropsEqualIgnoringForm);
EnquiryValuePanelBody.displayName = 'EnquiryValuePanelBody';

export const EnquiryValuePanel: React.FC<EnquiryValuePanelProps> = React.memo((panelProps) => (
    <FormSpy subscription={{ values: true }}>
        {({ values }) => (
            <EnquiryValuePanelBody
                {...panelProps}
                formValues={{
                    isEstimatedFeesIncludesTax: values.isEstimatedFeesIncludesTax,
                    isEstimatedDisbursementsIncludesTax: values.isEstimatedDisbursementsIncludesTax,
                }}
            />
        )}
    </FormSpy>
), arePropsEqualIgnoringForm);
EnquiryValuePanel.displayName = 'EnquiryValuePanel';
